/**
 * MODULE: domain/sales-presentations/frontend/components/CanvasSlideRenderer.tsx
 * PURPOSE: Canvas-based slide renderer – pixel-perfect output; strips markdown from text.
 * DEPENDENCIES: utils/markdown (stripMarkdown, parseMarkdownSegments), canvas-layer-drawing (drawLayers), platform/types (Layer)
 * PUBLIC API: CanvasSlideRenderer
 * SIDE_EFFECTS: canvas rendering, optional onRender callback
 * AGENT_SAFETY: 🟢 SAFE
 * Recent: 2026-02-26 [2.2.144] – Rich markdown (bold/italic) on canvas via parseMarkdownSegments; drawRichText for heading, subheading, lists.
 * Recent: 2026-05-13 – E6-1 canvas select or drag; E6-3 resize handles (12px hit targets), Shift
 *   aspect from drag-start bounds, min 40×20px, cursors, resizeLayerPreview vs commitLayerResize.
 * See: domain/sales-presentations/README.md
 */

import { useEffect, useMemo, useRef } from 'react';
import { stripMarkdown, parseMarkdownSegments } from '../utils/markdown';
import type { Layer } from '../../../../platform/types';
import {
  computeResizedBounds,
  cursorForHandle,
  drawLayers,
  getLayerBounds,
  hitTestHandle,
  hitTestLayer,
  type DrawLayersOptions,
  type HandleId,
} from './canvas-layer-drawing';

/** E6-1 / E6-3: store-driven canvas interaction; all pointer routing lives on this canvas. */
export interface CanvasSlideRendererInteractiveProps {
  slideId: string;
  selectedLayerId: string | null;
  layerDragPreview: { layerId: string; offsetX: number; offsetY: number } | null;
  layerResizePreview: {
    layerId: string;
    bounds: { x: number; y: number; width: number; height: number };
  } | null;
  onCanvasDragPhase?: (phase: 'move' | null) => void;
  onCanvasResizePhase?: (phase: 'resize' | null) => void;
  selectLayer: (layerId: string | null) => void;
  moveLayerPreview: (slideId: string, layerId: string, offsetX: number, offsetY: number) => void;
  clearLayerDragPreview: () => void;
  commitLayerMove: (slideId: string, layerId: string, finalXPct: number, finalYPct: number) => void;
  resizeLayerPreview: (
    slideId: string,
    layerId: string,
    bounds: { x: number; y: number; width: number; height: number }
  ) => void;
  clearLayerResizePreview: () => void;
  commitLayerResize: (
    slideId: string,
    layerId: string,
    bounds: { x: number; y: number; width: number; height: number },
    slideW: number,
    slideH: number
  ) => void;
  /**
   * E6-7: double-click on empty canvas (no layer hit) — slide-local pointer as percent of
   * logical width/height. Parent typically adds a text layer at this position.
   * // TODO(E6-9): auto-enter inline text edit on text layer add.
   */
  onCanvasBackgroundDoubleClick?: (slideId: string, xPct: number, yPct: number) => void;
}

interface CanvasSlideRendererProps {
  slide: Record<string, unknown>;
  width?: number;
  height?: number;
  scale?: number;
  onRender?: (canvas: HTMLCanvasElement) => void;
  /** When true, draws selection handles for `selectedLayerId` (editor only). Default false. */
  drawUI?: boolean;
  interactive?: CanvasSlideRendererInteractiveProps;
  className?: string;
  'data-testid'?: string;
}

export function CanvasSlideRenderer({
  slide,
  width = 960,
  height = 720,
  scale = 1,
  onRender,
  drawUI = false,
  interactive,
  className = 'rounded-lg shadow-lg',
  'data-testid': dataTestId,
}: CanvasSlideRendererProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const liveRef = useRef({ slide, width, height, interactive, drawUI });
  liveRef.current = { slide, width, height, interactive, drawUI };

  type CanvasDragState = {
    pointerId: number;
    layerId: string;
    slideId: string;
    startLocalX: number;
    startLocalY: number;
    originXPct: number;
    originYPct: number;
  };
  const dragRef = useRef<CanvasDragState | null>(null);

  type CanvasResizeState = {
    pointerId: number;
    slideId: string;
    layerId: string;
    handle: HandleId;
    startBounds: { x: number; y: number; width: number; height: number };
    startLocalX: number;
    startLocalY: number;
  };
  const resizeRef = useRef<CanvasResizeState | null>(null);

  const layerDrawOptions = useMemo((): DrawLayersOptions | undefined => {
    const layers = slide.layers;
    if (!Array.isArray(layers) || layers.length === 0) return undefined;
    const iv = interactive;
    const offsetMap =
      iv?.layerDragPreview != null
        ? new Map([
            [
              iv.layerDragPreview.layerId,
              { dx: iv.layerDragPreview.offsetX, dy: iv.layerDragPreview.offsetY },
            ],
          ])
        : null;
    const resizePx =
      iv?.layerResizePreview != null
        ? { layerId: iv.layerResizePreview.layerId, bounds: iv.layerResizePreview.bounds }
        : null;
    return {
      drawUI: Boolean(drawUI),
      selectedLayerId: iv?.selectedLayerId ?? null,
      layerDragOffsetPx: offsetMap,
      layerResizePreviewPx: resizePx,
    };
  }, [
    slide,
    drawUI,
    interactive?.selectedLayerId,
    interactive?.layerDragPreview?.layerId,
    interactive?.layerDragPreview?.offsetX,
    interactive?.layerDragPreview?.offsetY,
    interactive?.layerResizePreview?.layerId,
    interactive?.layerResizePreview?.bounds.x,
    interactive?.layerResizePreview?.bounds.y,
    interactive?.layerResizePreview?.bounds.width,
    interactive?.layerResizePreview?.bounds.height,
  ]);

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    canvas.width = width * scale;
    canvas.height = height * scale;

    ctx.scale(scale, scale);

    renderSlide(ctx, slide, width, height, layerDrawOptions);

    onRender?.(canvas);
  }, [slide, width, height, scale, onRender, layerDrawOptions]);

  useEffect(() => {
    const surface = canvasRef.current;
    const iv = interactive;
    if (surface == null || iv == null) return;
    /** Snapshot for listeners; avoids TS treating `ref.current` as nullable inside handlers. */
    const canvasEl = surface;

    function clientToLocal(clientX: number, clientY: number) {
      const r = canvasEl.getBoundingClientRect();
      const { width: w, height: h } = liveRef.current;
      const x = ((clientX - r.left) / (r.width || 1)) * w;
      const y = ((clientY - r.top) / (r.height || 1)) * h;
      return { x, y };
    }

    function visualBoundsForLayer(
      layer: Layer,
      sw: number,
      sh: number,
      dragP: CanvasSlideRendererInteractiveProps['layerDragPreview']
    ) {
      const b = getLayerBounds(layer, sw, sh);
      if (dragP && dragP.layerId === layer.id) {
        return { ...b, x: b.x + dragP.offsetX, y: b.y + dragP.offsetY };
      }
      return b;
    }

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      const { slide: sl, width: sw, height: sh, interactive: liveIv, drawUI: liveDrawUI } = liveRef.current;
      if (!liveIv) return;
      const layersRaw = sl.layers;
      const layersArr = Array.isArray(layersRaw) ? (layersRaw as Layer[]) : [];
      const { x, y } = clientToLocal(e.clientX, e.clientY);

      if (layersArr.length === 0) {
        liveIv.selectLayer(null);
        return;
      }

      // E6-3: selection handles can extend slightly past the layer bbox; test them before z-order body hits.
      if (liveDrawUI && liveIv.selectedLayerId) {
        const sel = layersArr.find((l) => l.id === liveIv.selectedLayerId);
        if (sel && !sel.locked && sel.visible) {
          const vb = visualBoundsForLayer(sel, sw, sh, liveIv.layerDragPreview);
          const handleHit = hitTestHandle(vb, x, y);
          if (handleHit) {
            liveIv.clearLayerDragPreview();
            liveIv.clearLayerResizePreview();
            if (typeof canvasEl.setPointerCapture === 'function') {
              canvasEl.setPointerCapture(e.pointerId);
            }
            resizeRef.current = {
              pointerId: e.pointerId,
              slideId: liveIv.slideId,
              layerId: sel.id,
              handle: handleHit,
              startBounds: { ...vb },
              startLocalX: x,
              startLocalY: y,
            };
            liveIv.onCanvasResizePhase?.('resize');
            return;
          }
        }
      }

      const ordered = [...layersArr].sort((a, b) => b.zIndex - a.zIndex);
      let hit: Layer | null = null;
      for (const layer of ordered) {
        if (hitTestLayer(layer, x, y, sw, sh)) {
          hit = layer;
          break;
        }
      }
      if (!hit) {
        liveIv.selectLayer(null);
        return;
      }

      liveIv.selectLayer(hit.id);
      if (hit.locked) return;

      liveIv.clearLayerResizePreview();
      if (typeof canvasEl.setPointerCapture === 'function') {
        canvasEl.setPointerCapture(e.pointerId);
      }
      dragRef.current = {
        pointerId: e.pointerId,
        layerId: hit.id,
        slideId: liveIv.slideId,
        startLocalX: x,
        startLocalY: y,
        originXPct: hit.x,
        originYPct: hit.y,
      };
      liveIv.onCanvasDragPhase?.('move');
    };

    const onPointerMove = (e: PointerEvent) => {
      const { interactive: liveIv, width: sw, height: sh, drawUI: liveDrawUI } = liveRef.current;
      if (!liveIv) return;

      const r = resizeRef.current;
      if (r && e.pointerId === r.pointerId) {
        const { x, y } = clientToLocal(e.clientX, e.clientY);
        const dx = x - r.startLocalX;
        const dy = y - r.startLocalY;
        const next = computeResizedBounds(r.startBounds, r.handle, dx, dy, e.shiftKey);
        liveIv.resizeLayerPreview(r.slideId, r.layerId, next);
        return;
      }

      const d = dragRef.current;
      if (d && e.pointerId === d.pointerId) {
        const { x, y } = clientToLocal(e.clientX, e.clientY);
        const dx = x - d.startLocalX;
        const dy = y - d.startLocalY;
        liveIv.moveLayerPreview(d.slideId, d.layerId, dx, dy);
        return;
      }

      if (!liveDrawUI || !liveIv.selectedLayerId) {
        canvasEl.style.cursor = '';
        return;
      }
      const layersArr = (liveRef.current.slide.layers ?? []) as Layer[];
      const sel = layersArr.find((l) => l.id === liveIv.selectedLayerId);
      if (!sel || sel.locked || !sel.visible) {
        canvasEl.style.cursor = '';
        return;
      }
      const { x, y } = clientToLocal(e.clientX, e.clientY);
      const vb = visualBoundsForLayer(sel, sw, sh, liveIv.layerDragPreview);
      const h = hitTestHandle(vb, x, y);
      if (h) {
        canvasEl.style.cursor = cursorForHandle(h);
        return;
      }
      if (hitTestLayer(sel, x, y, sw, sh)) {
        canvasEl.style.cursor = 'move';
        return;
      }
      canvasEl.style.cursor = '';
    };

    const finishDrag = (e: PointerEvent, commit: boolean) => {
      const d = dragRef.current;
      const { interactive: liveIv, width: sw, height: sh } = liveRef.current;
      if (!d || e.pointerId !== d.pointerId || !liveIv) return;
      if (typeof canvasEl.hasPointerCapture === 'function' && canvasEl.hasPointerCapture(e.pointerId)) {
        try {
          canvasEl.releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
      }
      dragRef.current = null;
      liveIv.onCanvasDragPhase?.(null);
      const { x, y } = clientToLocal(e.clientX, e.clientY);
      const dx = x - d.startLocalX;
      const dy = y - d.startLocalY;
      const moved = Math.hypot(dx, dy) > 2;
      if (!commit || !moved) {
        liveIv.clearLayerDragPreview();
        return;
      }
      const finalXPct = d.originXPct + (dx / sw) * 100;
      const finalYPct = d.originYPct + (dy / sh) * 100;
      liveIv.commitLayerMove(d.slideId, d.layerId, finalXPct, finalYPct);
    };

    const finishResize = (e: PointerEvent, commit: boolean) => {
      const r = resizeRef.current;
      const { interactive: liveIv, width: sw, height: sh } = liveRef.current;
      if (!r || e.pointerId !== r.pointerId || !liveIv) return;
      if (typeof canvasEl.hasPointerCapture === 'function' && canvasEl.hasPointerCapture(e.pointerId)) {
        try {
          canvasEl.releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
      }
      resizeRef.current = null;
      liveIv.onCanvasResizePhase?.(null);
      const { x, y } = clientToLocal(e.clientX, e.clientY);
      const dx = x - r.startLocalX;
      const dy = y - r.startLocalY;
      const moved = Math.hypot(dx, dy) > 2;
      if (!commit || !moved) {
        liveIv.clearLayerResizePreview();
        return;
      }
      const bounds = computeResizedBounds(r.startBounds, r.handle, dx, dy, e.shiftKey);
      liveIv.commitLayerResize(r.slideId, r.layerId, bounds, sw, sh);
    };

    const onPointerUp = (e: PointerEvent) => {
      if (resizeRef.current && e.pointerId === resizeRef.current.pointerId) {
        finishResize(e, true);
        return;
      }
      finishDrag(e, true);
    };

    const onPointerCancel = (e: PointerEvent) => {
      if (resizeRef.current && e.pointerId === resizeRef.current.pointerId) {
        finishResize(e, false);
        return;
      }
      finishDrag(e, false);
    };

    const onPointerLeave = () => {
      if (!dragRef.current && !resizeRef.current) {
        canvasEl.style.cursor = '';
      }
    };

    const onDoubleClick = (e: MouseEvent) => {
      const { slide: sl, width: sw, height: sh, interactive: liveIv } = liveRef.current;
      if (!liveIv?.onCanvasBackgroundDoubleClick) return;
      const layersRaw = sl.layers;
      const layersArr = Array.isArray(layersRaw) ? (layersRaw as Layer[]) : [];
      const { x, y } = clientToLocal(e.clientX, e.clientY);
      const xPct = (x / sw) * 100;
      const yPct = (y / sh) * 100;
      const ordered = [...layersArr].sort((a, b) => b.zIndex - a.zIndex);
      for (const layer of ordered) {
        if (layer.visible && !layer.locked && hitTestLayer(layer, x, y, sw, sh)) {
          return;
        }
      }
      liveIv.onCanvasBackgroundDoubleClick(liveIv.slideId, xPct, yPct);
    };

    canvasEl.addEventListener('pointerdown', onPointerDown);
    canvasEl.addEventListener('dblclick', onDoubleClick);
    canvasEl.addEventListener('pointermove', onPointerMove);
    canvasEl.addEventListener('pointerup', onPointerUp);
    canvasEl.addEventListener('pointercancel', onPointerCancel);
    canvasEl.addEventListener('pointerleave', onPointerLeave);
    return () => {
      dragRef.current = null;
      resizeRef.current = null;
      canvasEl.style.cursor = '';
      canvasEl.removeEventListener('pointerdown', onPointerDown);
      canvasEl.removeEventListener('dblclick', onDoubleClick);
      canvasEl.removeEventListener('pointermove', onPointerMove);
      canvasEl.removeEventListener('pointerup', onPointerUp);
      canvasEl.removeEventListener('pointercancel', onPointerCancel);
      canvasEl.removeEventListener('pointerleave', onPointerLeave);
    };
  }, [interactive?.slideId, interactive == null, interactive?.onCanvasBackgroundDoubleClick]);

  return (
    <canvas
      ref={canvasRef}
      data-testid={dataTestId}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        display: 'block',
        touchAction: interactive ? 'none' : undefined,
      }}
      className={className}
    />
  );
}

function normalizeSlideComponents(slide: Record<string, unknown>): Array<Record<string, unknown>> {
  if (Array.isArray(slide.components)) {
    return slide.components as Array<Record<string, unknown>>;
  }

  const components: Array<Record<string, unknown>> = [];

  if (slide.title) {
    components.push({
      type: 'heading',
      content: slide.title,
      position: { x: 0.5, y: 0.5, width: 9, height: 1 },
      style: { fontSize: 44, bold: true, alignment: 'center' },
    });
  }

  if (slide.content !== undefined && slide.content !== null && slide.content !== '') {
    const contentY = slide.title ? 2 : 0.5;
    if (Array.isArray(slide.content)) {
      components.push({
        type: 'bullet-list',
        content: slide.content,
        position: { x: 0.5, y: contentY, width: 9, height: 5 },
        style: { fontSize: 18 },
      });
    } else {
      components.push({
        type: 'text-block',
        content: String(slide.content),
        position: { x: 0.5, y: contentY, width: 9, height: 5 },
        style: { fontSize: 18 },
      });
    }
  }

  return components;
}

function renderSlide(
  ctx: CanvasRenderingContext2D,
  slide: Record<string, unknown>,
  width: number,
  height: number,
  drawLayerOptions?: DrawLayersOptions
) {
  ctx.clearRect(0, 0, width, height);

  const bgHex = (slide.background as { color?: string } | undefined)?.color ?? '';
  const bgColor = bgHex ? (bgHex.startsWith('#') ? bgHex : `#${bgHex}`) : '#FFFFFF';
  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, width, height);

  // Epic 3 layer model: if the slide has populated layers, those are the
  // canonical visual source — render them (in zIndex order). Legacy slides
  // (no layers) keep using the component-based rasterizer below.
  const layers = slide.layers;
  if (Array.isArray(layers) && layers.length > 0) {
    drawLayers(ctx, layers as Layer[], width, height, drawLayerOptions);
    return;
  }

  const components = normalizeSlideComponents(slide);
  for (const component of components) {
    renderComponent(ctx, component, width, height);
  }
}

const INCH_TO_PX = 96;

/**
 * Draw rich text (bold/italic) on canvas using parsed markdown segments.
 * Wraps at maxWidth; returns final y position.
 */
function drawRichText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  fontSize: number,
  lineHeight: number,
  options: { bold?: boolean; color?: string; alignment?: CanvasTextAlign } = {}
) {
  const segments = parseMarkdownSegments(text);
  const baseFont = 'Inter, sans-serif';
  let cx = x;
  let cy = y;
  const startX = x;
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  ctx.fillStyle = options.color ?? normColor('#1F2937');

  for (const seg of segments) {
    const weight = seg.bold || options.bold ? 'bold' : 'normal';
    const style = seg.italic ? 'italic' : 'normal';
    ctx.font = `${style} ${weight} ${fontSize}px ${baseFont}`;
    const metrics = ctx.measureText(seg.text);
    if (cx + metrics.width > startX + maxWidth && cx > startX) {
      cx = startX;
      cy += lineHeight;
    }
    ctx.fillText(seg.text, cx, cy);
    cx += metrics.width;
  }
  return cy + lineHeight;
}

function getPosition(
  position: unknown,
  slideWidth: number,
  slideHeight: number
): { x: number; y: number; width: number; height: number } {
  const p = position as { x?: number; y?: number; width?: number; height?: number } | null | undefined;
  return {
    x: (p?.x ?? 0) * INCH_TO_PX,
    y: (p?.y ?? 0) * INCH_TO_PX,
    width: (p?.width ?? slideWidth / INCH_TO_PX) * INCH_TO_PX,
    height: (p?.height ?? slideHeight / INCH_TO_PX) * INCH_TO_PX,
  };
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine + (currentLine ? ' ' : '') + word;
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

function normColor(c: unknown): string {
  if (typeof c !== 'string') return '#1F2937';
  return c.startsWith('#') ? c : `#${c}`;
}

function renderComponent(
  ctx: CanvasRenderingContext2D,
  component: Record<string, unknown>,
  slideWidth: number,
  slideHeight: number
) {
  const type = component.type as string;
  const renderers: Record<string, (c: CanvasRenderingContext2D, comp: Record<string, unknown>, w: number, h: number) => void> = {
    heading: renderHeading,
    subheading: renderSubheading,
    'text-block': renderTextBlock,
    'bullet-list': renderBulletList,
    'numbered-list': renderNumberedList,
    image: renderImage,
    'data-card': renderDataCard,
    table: renderTable,
    chart: renderChart,
  };
  const renderer = renderers[type];
  if (renderer) renderer(ctx, component, slideWidth, slideHeight);
}

function renderHeading(
  ctx: CanvasRenderingContext2D,
  component: Record<string, unknown>,
  slideWidth: number,
  slideHeight: number
) {
  const pos = getPosition(component.position, slideWidth, slideHeight);
  const style = (component.style ?? {}) as Record<string, unknown>;
  const fontSize = (style.fontSize as number) ?? 44;
  const lineHeight = fontSize * 1.3;
  ctx.save();
  drawRichText(
    ctx,
    String(component.content ?? ''),
    pos.x,
    pos.y,
    pos.width,
    fontSize,
    lineHeight,
    { bold: true, color: normColor(style.color ?? '#1F2937') }
  );
  ctx.restore();
}

function renderSubheading(
  ctx: CanvasRenderingContext2D,
  component: Record<string, unknown>,
  slideWidth: number,
  slideHeight: number
) {
  const pos = getPosition(component.position, slideWidth, slideHeight);
  const style = (component.style ?? {}) as Record<string, unknown>;
  const fontSize = (style.fontSize as number) ?? 28;
  const lineHeight = fontSize * 1.3;
  ctx.save();
  drawRichText(
    ctx,
    String(component.content ?? ''),
    pos.x,
    pos.y,
    pos.width,
    fontSize,
    lineHeight,
    {
      bold: typeof style.bold === 'boolean' ? style.bold : true,
      color: normColor(style.color ?? '#4B5563'),
    }
  );
  ctx.restore();
}

function renderTextBlock(
  ctx: CanvasRenderingContext2D,
  component: Record<string, unknown>,
  slideWidth: number,
  slideHeight: number
) {
  const pos = getPosition(component.position, slideWidth, slideHeight);
  const style = (component.style ?? {}) as Record<string, unknown>;
  const fontSize = (style.fontSize as number) ?? 18;
  ctx.save();
  ctx.font = `${fontSize}px Inter, sans-serif`;
  ctx.fillStyle = normColor(style.color ?? '#1F2937');
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  const lines = wrapText(ctx, stripMarkdown(String(component.content ?? '')), pos.width);
  const lineHeight = fontSize * 1.5;
  let y = pos.y;
  for (const line of lines) {
    if (y + lineHeight > pos.y + pos.height) break;
    ctx.fillText(line, pos.x, y);
    y += lineHeight;
  }
  ctx.restore();
}

function renderBulletList(
  ctx: CanvasRenderingContext2D,
  component: Record<string, unknown>,
  slideWidth: number,
  slideHeight: number
) {
  const pos = getPosition(component.position, slideWidth, slideHeight);
  const style = (component.style ?? {}) as Record<string, unknown>;
  const fontSize = (style.fontSize as number) ?? 18;
  const raw = component.content;
  const items = Array.isArray(raw)
    ? (raw as unknown[]).map((r) => String(r ?? ''))
    : [String(raw ?? '')];
  ctx.save();
  ctx.fillStyle = normColor(style.color ?? '#1F2937');
  const lineHeight = fontSize * 1.8;
  const bulletX = pos.x + 10;
  const textX = pos.x + 30;
  const textWidth = pos.width - (textX - pos.x);
  let y = pos.y;
  for (const item of items) {
    if (y + lineHeight > pos.y + pos.height) break;
    ctx.beginPath();
    ctx.arc(bulletX, y + fontSize / 2, 4, 0, Math.PI * 2);
    ctx.fill();
    y = drawRichText(ctx, item, textX, y, textWidth, fontSize, lineHeight, {
      color: normColor(style.color ?? '#1F2937'),
    });
  }
  ctx.restore();
}

function renderNumberedList(
  ctx: CanvasRenderingContext2D,
  component: Record<string, unknown>,
  slideWidth: number,
  slideHeight: number
) {
  const pos = getPosition(component.position, slideWidth, slideHeight);
  const style = (component.style ?? {}) as Record<string, unknown>;
  const fontSize = (style.fontSize as number) ?? 18;
  const raw = component.content;
  const items = Array.isArray(raw)
    ? (raw as unknown[]).map((r) => String(r ?? ''))
    : [String(raw ?? '')];
  ctx.save();
  ctx.font = `${fontSize}px Inter, sans-serif`;
  ctx.fillStyle = normColor(style.color ?? '#1F2937');
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  const lineHeight = fontSize * 1.8;
  const textX = pos.x + 30;
  const textWidth = pos.width - (textX - pos.x);
  let y = pos.y;
  items.forEach((item, index) => {
    if (y + lineHeight > pos.y + pos.height) return;
    ctx.fillText(`${index + 1}.`, pos.x + 10, y);
    y = drawRichText(ctx, item, textX, y, textWidth, fontSize, lineHeight, {
      color: normColor(style.color ?? '#1F2937'),
    });
  });
  ctx.restore();
}

function renderImage(
  ctx: CanvasRenderingContext2D,
  component: Record<string, unknown>,
  slideWidth: number,
  slideHeight: number
) {
  const pos = getPosition(component.position, slideWidth, slideHeight);
  const content = component.content;
  const imageUrl = typeof content === 'object' && content !== null && 'url' in content
    ? (content as { url: string }).url
    : typeof content === 'string'
      ? content
      : '';
  if (!imageUrl) return;
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    ctx.drawImage(img, pos.x, pos.y, pos.width, pos.height);
  };
  img.onerror = () => {
    ctx.fillStyle = '#E5E7EB';
    ctx.fillRect(pos.x, pos.y, pos.width, pos.height);
    ctx.fillStyle = '#6B7280';
    ctx.font = '14px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Image', pos.x + pos.width / 2, pos.y + pos.height / 2);
  };
  img.src = imageUrl;
}

function renderDataCard(
  ctx: CanvasRenderingContext2D,
  component: Record<string, unknown>,
  slideWidth: number,
  slideHeight: number
) {
  const pos = getPosition(component.position, slideWidth, slideHeight);
  const content = (component.content ?? {}) as Record<string, unknown>;
  ctx.save();
  const radius = 10;
  ctx.fillStyle = '#EFF6FF';
  ctx.strokeStyle = '#3B82F6';
  ctx.lineWidth = 2;
  roundRect(ctx, pos.x, pos.y, pos.width, pos.height, radius);
  ctx.fill();
  ctx.stroke();
  const metric = String(content.metric ?? '');
  ctx.font = 'bold 48px Inter, sans-serif';
  ctx.fillStyle = '#3B82F6';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(metric, pos.x + pos.width / 2, pos.y + pos.height / 2 - 20);
  const label = String(content.label ?? '');
  if (label) {
    ctx.font = '16px Inter, sans-serif';
    ctx.fillStyle = '#4B5563';
    ctx.fillText(label, pos.x + pos.width / 2, pos.y + pos.height / 2 + 30);
  }
  ctx.restore();
}

function renderTable(
  ctx: CanvasRenderingContext2D,
  component: Record<string, unknown>,
  slideWidth: number,
  slideHeight: number
) {
  const pos = getPosition(component.position, slideWidth, slideHeight);
  const content = (component.content ?? {}) as Record<string, unknown>;
  const rows = (content.rows as unknown[][]) ?? [];
  if (rows.length === 0) return;
  const cols = rows[0].length;
  const cellWidth = pos.width / cols;
  const cellHeight = 30;
  ctx.save();
  ctx.font = '14px Inter, sans-serif';
  ctx.textBaseline = 'middle';
  rows.forEach((row: unknown[], rowIndex: number) => {
    const y = pos.y + rowIndex * cellHeight;
    if (rowIndex === 0) {
      ctx.fillStyle = '#5B21B6';
      ctx.fillRect(pos.x, y, pos.width, cellHeight);
    }
    row.forEach((cell, colIndex) => {
      const x = pos.x + colIndex * cellWidth;
      ctx.strokeStyle = '#000000';
      ctx.strokeRect(x, y, cellWidth, cellHeight);
      ctx.fillStyle = rowIndex === 0 ? '#FFFFFF' : '#000000';
      ctx.textAlign = 'center';
      ctx.fillText(String(cell), x + cellWidth / 2, y + cellHeight / 2);
    });
  });
  ctx.restore();
}

function renderChart(
  ctx: CanvasRenderingContext2D,
  component: Record<string, unknown>,
  slideWidth: number,
  slideHeight: number
) {
  const pos = getPosition(component.position, slideWidth, slideHeight);
  ctx.save();
  ctx.fillStyle = '#F3F4F6';
  ctx.fillRect(pos.x, pos.y, pos.width, pos.height);
  ctx.strokeStyle = '#D1D5DB';
  ctx.strokeRect(pos.x, pos.y, pos.width, pos.height);
  ctx.fillStyle = '#6B7280';
  ctx.font = '16px Inter, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('Chart Placeholder', pos.x + pos.width / 2, pos.y + pos.height / 2);
  ctx.restore();
}
